// feedback.ts — product feedback board (mirrors backend apps/feedback serializers).
//
// Deliberately unrelated to core/models/case.ts: a feature request is not a
// moderation case and must never borrow that vocabulary.

export type FeedbackCategory =
  | 'bug'
  | 'feature'
  | 'performance'
  | 'ux'
  | 'accessibility'
  | 'moderation'
  | 'other';

export type FeedbackStatus =
  | 'open'
  | 'under-review'
  | 'planned'
  | 'in-progress'
  | 'testing'
  | 'shipped'
  | 'resolved'
  | 'closed'
  | 'declined';

export type FeedbackPriority = 'none' | 'low' | 'medium' | 'high' | 'critical';

export type FeedbackPlatform = 'unspecified' | 'web' | 'mobile' | 'both';

export type FeedbackStageState = 'pending' | 'in_progress' | 'done';

/** '' means "not answered" — mirrors the backend's blank CharField default. */
export type FeedbackFrequency = 'always' | 'sometimes' | 'rarely' | 'once' | '';

/**
 * Standard-browser-API device snapshot (1.21.50) — see
 * feedback/lib/deviceSnapshot.ts for how this is captured. Every field is
 * optional: a missing browser API (or the reporter deleting one field)
 * degrades to an absent key, never an error or a placeholder value.
 * Mirrors backend structured_fields.DEVICE_SNAPSHOT_ALLOWED_KEYS exactly —
 * the server drops anything outside this key set.
 */
export interface FeedbackDeviceSnapshot {
  cpuCores?: number;
  ramTier?: string;
  gpu?: string;
  screenWidth?: number;
  screenHeight?: number;
  devicePixelRatio?: number;
  connection?: string;
}

/** Categories surfaced in the Council (moderator) view rather than the dev tracker. */
export const COUNCIL_CATEGORIES: FeedbackCategory[] = ['moderation'];

/** Statuses that count toward the "completed" stats tile (see backend COMPLETED_STATUSES). */
export const FEEDBACK_COMPLETED_STATUSES: FeedbackStatus[] = ['shipped', 'resolved', 'closed'];

/** Statuses grouped onto the roadmap page / preview rail, in display order. */
export const ROADMAP_STATUSES: FeedbackStatus[] = ['planned', 'in-progress', 'testing', 'shipped'];

export interface FeedbackAuthor {
  id: string;
  name: string;
  handle: string;
  /** Resolved avatar URL or a gradient key — Avatar accepts either. */
  av: string;
  verified: boolean;
  isStaff: boolean;
}

export interface FeedbackComment {
  id: string;
  author: FeedbackAuthor;
  body: string;
  /** Staff-only note. The backend never serializes these to a non-staff viewer. */
  internal: boolean;
  createdAt: string;
  helpfulCount: number;
  /** Whether the current viewer marked this comment helpful. Always false signed out. */
  helpful: boolean;
}

export interface FeedbackStage {
  id: number;
  title: string;
  position: number;
  state: FeedbackStageState;
  completedAt: string | null;
  eta: string | null;
  progress_pct: number;
  locked: boolean;
}

export interface FeedbackRoadmap {
  milestoneLabel: string;
  milestoneEta: string | null;
  publicUpdate: string;
  overallProgress: number;
  stages: FeedbackStage[];
}

export interface FeedbackWatchers {
  count: number;
  avatars: FeedbackAuthor[];
  watching: boolean;
}

export interface FeedbackRelatedItem {
  id: string;
  title: string;
  status: FeedbackStatus;
}

export interface FeedbackAttachment {
  id: number;
  url: string;
  filename: string;
  size: number;
  content_type: string;
  uploader: FeedbackAuthor;
  createdAt: string;
}

export type FeedbackActivityVerb =
  | 'created'
  | 'status_changed'
  | 'priority_changed'
  | 'assigned'
  | 'pinned'
  | 'unpinned'
  | 'stage_added'
  | 'stage_updated'
  | 'stage_completed'
  | 'stage_removed'
  | 'stage_reordered'
  | 'roadmap_updated'
  | 'relation_added'
  | 'relation_removed'
  | 'fields_updated';

export interface FeedbackActivityEntry {
  id: number;
  verb: FeedbackActivityVerb;
  detail: Record<string, unknown>;
  actor: FeedbackAuthor | null;
  createdAt: string;
}

/**
 * Fields present on the LIST endpoint's card payload — see backend
 * FeedbackItemListSerializer, which is deliberately a much lighter shape than
 * the detail endpoint (no statusNote/assignedTo/productArea/foundInVersion/
 * details/roadmap fields/watcherCount/canManage — those are detail-only).
 * FeedbackItem below adds everything the detail endpoint includes on top of
 * this.
 */
export interface FeedbackListItem {
  id: string;
  author: FeedbackAuthor;
  title: string;
  body: string;
  category: FeedbackCategory;
  status: FeedbackStatus;
  priority: FeedbackPriority;
  pinned: boolean;
  upvotes: number;
  /** Whether the current viewer has upvoted. Always false when signed out. */
  upvoted: boolean;
  commentCount: number;
  createdAt: string;
  platform: FeedbackPlatform;
  /** Up to 3 distinct recent public-commenter avatars (see FeedbackCard). */
  participants: FeedbackAuthor[];
  /** Staff-hidden for abuse. Only staff ever receive an item with this true. */
  removed: boolean;
}

/** Detail-only fields — see backend FeedbackItemSerializer's additions over
 * FeedbackItemListSerializer. Only ever present on a GET /feedback/{id}/ (or
 * any mutation response, which always re-serializes the full detail shape)
 * result — never on a list/peek/search read. */
export interface FeedbackItem extends FeedbackListItem {
  statusNote: string;
  assignedTo: FeedbackAuthor | null;
  productArea: string;
  foundInVersion: string;
  /**
   * Per-topic structured answers, keyed by field id (see
   * feedbackMeta.FEEDBACK_TOPIC_FIELDS). Staff-only keys are simply absent
   * from a payload served to a non-staff viewer — the server filters them,
   * this type does not distinguish "absent" from "never answered".
   */
  details: Record<string, string>;
  milestoneLabel: string;
  milestoneEta: string | null;
  publicUpdate: string;
  overallProgress: number;
  watcherCount: number;
  /** Client hint — the server is still the real gate on every mutation. */
  canManage: boolean;
  comments: FeedbackComment[];
  roadmap: FeedbackRoadmap;
  watchers: FeedbackWatchers;
  related: FeedbackRelatedItem[];
  attachments: FeedbackAttachment[];
  activity: FeedbackActivityEntry[];
  /** Detail-only stat — a plain view COUNT, never a per-viewer log (1.21.55).
   * Incremented server-side on GET; see backend FeedbackViewSet._register_view. */
  views: number;
  /** Detail-only (1.21.55) — mirrors mobile's More tab metadata field; not
   * yet rendered anywhere on web. See backend FeedbackItemSerializer.updatedAt. */
  updatedAt: string;

  // ── structured fields (1.21.50) — real columns, always public ──────────
  /** Bug/performance only on write; may still read back for older items. */
  expectedBehavior: string;
  actualBehavior: string;
  /** One step per line — rendered as a numbered list. */
  stepsToReproduce: string;
  additionalContext: string;
  frequency: FeedbackFrequency;
  /** Free text parsed from the reporter's user agent, editable before submit. */
  devicePlatform: string;
  browser: string;
  osVersion: string;
  deviceSnapshot: FeedbackDeviceSnapshot;
  submittedTz: string;
  /** Curated free labels — staff/assigned-contributor managed, visible to everyone. */
  tags: string[];
}

/** Response shape of POST /feedback/attachments/draft/ — an inline image
 * uploaded before the item exists (see feedback/lib/markdown editor). */
export interface FeedbackDraftAttachment {
  id: number;
  url: string;
  filename: string;
  content_type: string;
  size: number;
}

export interface CreateFeedbackInput {
  title: string;
  body: string;
  category: FeedbackCategory;
  /** Only keys valid for `category` should be sent — extras are stripped server-side. */
  details?: Record<string, string>;
  platform?: FeedbackPlatform;
  product_area?: string;
  found_in_version?: string;

  // ── structured fields (1.21.50) — flat top-level keys, all optional.
  // expected_behavior/actual_behavior/steps_to_reproduce/frequency are only
  // stored for category 'bug' | 'performance' — sending them for any other
  // category is silently dropped server-side, never an error.
  expected_behavior?: string;
  actual_behavior?: string;
  steps_to_reproduce?: string;
  frequency?: FeedbackFrequency;
  additional_context?: string;
  device_platform?: string;
  browser?: string;
  os_version?: string;
  submitted_tz?: string;
  /** Captured client-side only, sent ONLY inside this create POST — see
   * feedback/lib/deviceSnapshot.ts. Omitted entirely (not `{}`) when the
   * reporter excludes device info. */
  device_snapshot?: FeedbackDeviceSnapshot;
  /** Ids from POST /feedback/attachments/draft/ (inline images inserted into
   * the markdown body) to claim onto the new item — see SubmitFeedbackPanel. */
  draft_attachment_ids?: number[];
}

export interface FeedbackStats {
  categoryCounts?: Partial<Record<FeedbackCategory, number>>;
  updatedThisWeek?: number;
  total: number;
  open: number;
  underReview: number;
  planned: number;
  inProgress: number;
  testing: number;
  completed: number;
  declined: number;
  newThisWeek: number;
}

export interface FeedbackManageInput {
  priority?: FeedbackPriority;
  product_area?: string;
  platform?: FeedbackPlatform;
  found_in_version?: string;
  status_note?: string;
  /** Staff-only. */
  pinned?: boolean;
  /** Staff-only. `"@handle"` to assign, `""` to unassign. */
  assigned_to?: string;
}

export interface FeedbackRoadmapInput {
  milestone_label?: string;
  milestone_eta?: string | null;
  public_update?: string;
}

export interface FeedbackStageInput {
  title?: string;
  eta?: string | null;
  progress_pct?: number;
  /** Staff-only. */
  locked?: boolean;
  state?: FeedbackStageState;
}

export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  bug: 'Bug',
  feature: 'Feature request',
  performance: 'Performance',
  ux: 'UI / UX',
  accessibility: 'Accessibility',
  moderation: 'Moderation policy',
  other: 'Other',
};

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  'open': 'Open',
  'under-review': 'Under review',
  'planned': 'Planned',
  'in-progress': 'In progress',
  'testing': 'Testing',
  'shipped': 'Shipped',
  'resolved': 'Resolved',
  'closed': 'Closed',
  'declined': 'Declined',
};

export const FEEDBACK_PRIORITY_LABELS: Record<FeedbackPriority, string> = {
  none: 'None',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export const FEEDBACK_PLATFORM_LABELS: Record<FeedbackPlatform, string> = {
  unspecified: 'Unspecified',
  web: 'Web',
  mobile: 'Mobile',
  both: 'Both',
};

export const FEEDBACK_CATEGORIES = Object.keys(FEEDBACK_CATEGORY_LABELS) as FeedbackCategory[];
export const FEEDBACK_STATUSES = Object.keys(FEEDBACK_STATUS_LABELS) as FeedbackStatus[];
export const FEEDBACK_PRIORITIES = Object.keys(FEEDBACK_PRIORITY_LABELS) as FeedbackPriority[];
export const FEEDBACK_PLATFORMS = Object.keys(FEEDBACK_PLATFORM_LABELS) as FeedbackPlatform[];
